import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Pagination,
  LinearProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DashboardIcon from '@mui/icons-material/Dashboard';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
} from 'recharts';

import {
  useFetchDashboardSnapshot,
  useFetchDashboardTicketsActivos,
  useDescargarReporteDashboard,
  useFetchTicketEstados,
  useFetchTicketPrioridades,
  useFetchTicketAreas,
  type IDashboardFiltros,
} from '@/shared/actions/erp/tickets-soporte-cliente';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';

const REFRESH_OPTIONS = [
  { label: 'Cada 15s', value: 15000 },
  { label: 'Cada 30s', value: 30000 },
  { label: 'Cada 60s', value: 60000 },
  { label: 'Pausado', value: 0 },
];

const FALLBACK_COLORS = ['#1976d2', '#9c27b0', '#ed6c02', '#2e7d32', '#d32f2f', '#0288d1', '#7b1fa2', '#388e3c', '#f57c00', '#c62828'];

export default function DashboardSupervisorPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  // user esta disponible para futuras ramas (ej. mostrar nombre del
  // supervisor en el header). No se usa hoy pero el subscribe se mantiene
  // para no romper la integridad del store si alguien lo agrega.
  void user;

  // Catalogos para los filtros (estado, prioridad, area).
  const { data: dataEstados } = useFetchTicketEstados({ page_size: 50 });
  const { data: dataPrioridades } = useFetchTicketPrioridades({ page_size: 50 });
  const { data: dataAreas } = useFetchTicketAreas({ page_size: 50 });
  const estados = (dataEstados as any)?.data || [];
  const prioridades = (dataPrioridades as any)?.data || [];
  const areas = (dataAreas as any)?.data || [];

  // Filtros del dashboard
  const [filtros, setFiltros] = useState<IDashboardFiltros>({ solo_abiertos: true });
  const [refreshMs, setRefreshMs] = useState<number>(15000);

  // Tabla paginada
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Reset de pagina cuando cambian filtros (igual que en mis-tickets).
  const filtrosKey = JSON.stringify(filtros);
  useEffect(() => {
    setPage(1);
  }, [filtrosKey]);

  // Hooks de datos.
  const refreshInterval = refreshMs > 0 ? refreshMs : false;
  const { data: snapshotResp, isLoading: loadingSnap, isFetching: fetchingSnap, dataUpdatedAt, refetch: refetchSnapshot } =
    useFetchDashboardSnapshot(filtros, refreshInterval);
  const snapshot = (snapshotResp as any)?.data;

  const { data: ticketsResp, isLoading: loadingTickets, isFetching: fetchingTickets, refetch: refetchTickets } =
    useFetchDashboardTicketsActivos(filtros, page, pageSize, refreshInterval);
  const ticketsRows = (ticketsResp as any)?.data || [];
  const ticketsTotal = (ticketsResp as any)?.total || 0;

  const exportMutation = useDescargarReporteDashboard();

  // "Actualizado hace Xs" — recalcula cada segundo para que el reloj corra.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const segundosDesdeUpdate = dataUpdatedAt ? Math.max(0, Math.floor((now - dataUpdatedAt) / 1000)) : 0;

  // Charts: paleta consistente entre poligono y leyenda.
  const colorFor = (b: any, idx: number) =>
    (b?.color && /^#?[0-9a-f]{3,8}$/i.test(b.color)) ? b.color : FALLBACK_COLORS[idx % FALLBACK_COLORS.length];

  const onClearFiltros = () => setFiltros({ solo_abiertos: true });

  const handleDescargar = () => {
    // El export tiene su propio default solo_abiertos=false porque si filtras
    // por "solo abiertos" pero quieres reporte historico, normalmente lo
    // querras con cerrados tambien. El usuario puede seguir filtrando dates.
    exportMutation.mutate({ ...filtros, solo_abiertos: false });
  };

  // Formatea un timestamp ISO -> hora local cortita.
  const fmtFecha = (s: string | null | undefined) => {
    if (!s) return '-';
    return new Date(s).toLocaleString('es-EC', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  };

  // SLA helper: el snapshot trae sla_habilitado; si esta off, no marcamos
  // filas en rojo. Si esta on, comparamos fecha_limite_sla < ahora.
  const slaHabilitado = !!snapshot?.sla_habilitado;
  const isSlaVencido = (row: any) => {
    if (!slaHabilitado || !row.fecha_limite_sla) return false;
    return new Date(row.fecha_limite_sla).getTime() < Date.now();
  };

  // Memo: data lista para los charts.
  const dataPorEstado = useMemo(() => snapshot?.por_estado || [], [snapshot]);
  const dataPorPrioridad = useMemo(() => snapshot?.por_prioridad || [], [snapshot]);
  const dataPorArea = useMemo(() => snapshot?.por_area || [], [snapshot]);
  const dataAsesores = useMemo(() => snapshot?.top_asesores || [], [snapshot]);
  const dataAsesoresPorEstado = useMemo(() => snapshot?.asesores_por_estado || [], [snapshot]);

  // Construimos las columnas dinamicas de la tabla "asesor x estado":
  // unique de todos los estados que aparecen en cualquier asesor, ordenados
  // por orden de aparicion del snapshot.por_estado (que ya viene ordenado
  // por total descendente). Asi las columnas mas relevantes salen primero.
  const estadosColumnas = useMemo(() => {
    const map = new Map<string, { id: string; label: string; color: string }>();
    // 1) precarga con orden del breakdown global por estado
    for (const b of (snapshot?.por_estado || [])) {
      map.set(b.id, { id: b.id, label: b.label, color: b.color });
    }
    // 2) sumar los que aparecen solo en asesores_por_estado pero no en el global
    for (const a of dataAsesoresPorEstado) {
      for (const e of (a.por_estado || [])) {
        if (!map.has(e.id)) map.set(e.id, { id: e.id, label: e.label, color: e.color });
      }
    }
    return Array.from(map.values());
  }, [snapshot, dataAsesoresPorEstado]);

  // Filtro de busqueda dentro de la tabla asesor x estado.
  const [busquedaAsesor, setBusquedaAsesor] = useState('');
  const dataAsesoresFiltrada = useMemo(() => {
    const q = busquedaAsesor.trim().toLowerCase();
    if (!q) return dataAsesoresPorEstado;
    return dataAsesoresPorEstado.filter((a: any) =>
      (a.usuario_nombre || '').toLowerCase().includes(q),
    );
  }, [busquedaAsesor, dataAsesoresPorEstado]);

  // Loading state inicial: el snapshot aun no llego.
  if (loadingSnap && !snapshot) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }}
             justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <DashboardIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Dashboard Supervisor de Tickets
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {refreshInterval
                ? `Actualizado hace ${segundosDesdeUpdate}s · Próx. ${Math.max(0, Math.round(refreshMs / 1000) - segundosDesdeUpdate)}s`
                : 'Actualizacion pausada'}
              {(fetchingSnap || fetchingTickets) && ' · refrescando...'}
              {!slaHabilitado && ' · SLA desactivado globalmente'}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Refresh</InputLabel>
            <Select
              value={refreshMs}
              label="Refresh"
              onChange={(e: SelectChangeEvent<number>) => setRefreshMs(Number(e.target.value))}
            >
              {REFRESH_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title={refreshInterval ? 'Pausar' : 'Reanudar'}>
            <IconButton onClick={() => setRefreshMs(refreshMs > 0 ? 0 : 15000)} size="small">
              {refreshInterval ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Refrescar ahora">
            <IconButton onClick={() => { refetchSnapshot(); refetchTickets(); }} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={exportMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
            disabled={exportMutation.isPending}
            onClick={handleDescargar}
          >
            {exportMutation.isPending ? 'Generando...' : 'Descargar XLSX'}
          </Button>
          <Button variant="outlined" size="small" onClick={() => navigate(-1)}>
            Volver
          </Button>
        </Stack>
      </Stack>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 2 }} elevation={1}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select value={filtros.estado_ticket || ''} label="Estado"
                onChange={(e: SelectChangeEvent) => setFiltros({ ...filtros, estado_ticket: (e.target.value as string) || undefined })}>
                <MenuItem value="">Todos</MenuItem>
                {estados.map((e: any) => (
                  <MenuItem key={e.id_ticket_estado} value={e.id_ticket_estado}>{e.nombre}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Prioridad</InputLabel>
              <Select value={filtros.prioridad || ''} label="Prioridad"
                onChange={(e: SelectChangeEvent) => setFiltros({ ...filtros, prioridad: (e.target.value as string) || undefined })}>
                <MenuItem value="">Todas</MenuItem>
                {prioridades.map((p: any) => (
                  <MenuItem key={p.id_ticket_prioridad} value={p.id_ticket_prioridad}>{p.nombre}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Area</InputLabel>
              <Select value={filtros.area_destino || ''} label="Area"
                onChange={(e: SelectChangeEvent) => setFiltros({ ...filtros, area_destino: (e.target.value as string) || undefined })}>
                <MenuItem value="">Todas</MenuItem>
                {areas.map((a: any) => (
                  <MenuItem key={a.id_area} value={a.id_area}>{a.nombre}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField fullWidth size="small" type="date" label="Desde"
              InputLabelProps={{ shrink: true }}
              value={filtros.fecha_desde || ''}
              onChange={(e) => setFiltros({ ...filtros, fecha_desde: e.target.value || undefined })}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField fullWidth size="small" type="date" label="Hasta"
              InputLabelProps={{ shrink: true }}
              value={filtros.fecha_hasta || ''}
              onChange={(e) => setFiltros({ ...filtros, fecha_hasta: e.target.value || undefined })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Stack direction="row" spacing={1}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Estado vista</InputLabel>
                <Select
                  value={filtros.solo_abiertos ? 'abiertos' : 'todos'}
                  label="Estado vista"
                  onChange={(e: SelectChangeEvent) => setFiltros({ ...filtros, solo_abiertos: e.target.value === 'abiertos' })}
                >
                  <MenuItem value="abiertos">Solo abiertos</MenuItem>
                  <MenuItem value="todos">Todos</MenuItem>
                </Select>
              </FormControl>
              <Button size="small" variant="outlined" onClick={onClearFiltros}>Limpiar</Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <KpiCard label="Abiertos" value={snapshot?.kpis?.abiertos ?? 0} color="primary" />
        <KpiCard label="Cerrados hoy" value={snapshot?.kpis?.cerrados_hoy ?? 0} color="success" />
        {slaHabilitado ? (
          <KpiCard
            label="SLA vencidos"
            value={snapshot?.kpis?.sla_vencidos ?? 0}
            color={(snapshot?.kpis?.sla_vencidos ?? 0) > 0 ? 'error' : 'success'}
            icon={(snapshot?.kpis?.sla_vencidos ?? 0) > 0 ? <WarningAmberIcon fontSize="large" /> : undefined}
          />
        ) : (
          <KpiCard label="SLA" value="OFF" color="default" />
        )}
        <KpiCard label="Total filtrado" value={snapshot?.kpis?.total ?? 0} color="default" />
      </Grid>

      {/* Charts */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 360 }} elevation={1}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Top asesores ({dataAsesores.length})
            </Typography>
            {dataAsesores.length === 0 ? (
              <Alert severity="info" variant="outlined">Sin datos para los filtros aplicados.</Alert>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dataAsesores.slice(0, 15)} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="usuario_nombre" width={130} tick={{ fontSize: 11 }} />
                  <RechartsTooltip />
                  <Bar dataKey="count" fill="#1976d2" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, height: 360 }} elevation={1}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Por estado</Typography>
            {dataPorEstado.length === 0 ? (
              <Alert severity="info" variant="outlined">Sin datos.</Alert>
            ) : (
              <ResponsiveContainer width="100%" height={290}>
                <PieChart>
                  <Pie data={dataPorEstado} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.count}`}>
                    {dataPorEstado.map((b: any, i: number) => <Cell key={b.id} fill={colorFor(b, i)} />)}
                  </Pie>
                  <RechartsTooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, height: 360 }} elevation={1}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Por prioridad</Typography>
            {dataPorPrioridad.length === 0 ? (
              <Alert severity="info" variant="outlined">Sin datos.</Alert>
            ) : (
              <ResponsiveContainer width="100%" height={290}>
                <PieChart>
                  <Pie data={dataPorPrioridad} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.count}`}>
                    {dataPorPrioridad.map((b: any, i: number) => <Cell key={b.id} fill={colorFor(b, i)} />)}
                  </Pie>
                  <RechartsTooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, height: 280 }} elevation={1}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Tickets por area</Typography>
            {dataPorArea.length === 0 ? (
              <Alert severity="info" variant="outlined">Sin datos.</Alert>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={dataPorArea}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={60} />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="count" fill="#9c27b0" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Asesores del departamento x estado de sus tickets */}
      <Paper sx={{ p: 2, mb: 2 }} elevation={1}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }}
               justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Asesores por estado ({dataAsesoresPorEstado.length})
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {filtros.area_destino
                ? 'Asesores del área seleccionada y cuántos tickets tienen en cada estado.'
                : 'Selecciona un área en los filtros para ver solo asesores de ese departamento.'}
            </Typography>
          </Box>
          <TextField
            size="small"
            placeholder="Buscar asesor..."
            value={busquedaAsesor}
            onChange={(e) => setBusquedaAsesor(e.target.value)}
            sx={{ minWidth: 220 }}
          />
        </Stack>
        {dataAsesoresFiltrada.length === 0 ? (
          <Alert severity="info" variant="outlined">
            {busquedaAsesor ? 'Ningún asesor coincide con la búsqueda.' : 'Sin asesores con tickets para los filtros aplicados.'}
          </Alert>
        ) : (
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>Asesor</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                  {estadosColumnas.map((est) => (
                    <TableCell key={est.id} align="center" sx={{ fontWeight: 700 }}>
                      <Chip
                        label={est.label}
                        size="small"
                        sx={{
                          bgcolor: est.color || '#9e9e9e',
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {dataAsesoresFiltrada.map((asesor: any) => {
                  // Mapa { estado_id -> count } para acceso O(1) al renderizar.
                  const porEstadoMap = new Map<string, number>();
                  for (const e of (asesor.por_estado || [])) {
                    porEstadoMap.set(e.id, e.count);
                  }
                  return (
                    <TableRow key={asesor.usuario_id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{asesor.usuario_nombre || '(Sin nombre)'}</Typography>
                        <Typography variant="caption" color="text.secondary">ID: {asesor.usuario_id}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700} color="primary.main">{asesor.total}</Typography>
                      </TableCell>
                      {estadosColumnas.map((est) => {
                        const count = porEstadoMap.get(est.id) || 0;
                        return (
                          <TableCell key={est.id} align="center">
                            {count > 0 ? (
                              <Chip
                                label={count}
                                size="small"
                                sx={{
                                  bgcolor: est.color || '#9e9e9e',
                                  color: '#fff',
                                  fontWeight: 700,
                                  minWidth: 36,
                                }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Tabla de tickets activos */}
      <Paper sx={{ p: 2 }} elevation={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Tickets activos ({ticketsTotal})
          </Typography>
          {fetchingTickets && <Typography variant="caption" color="text.secondary">Refrescando...</Typography>}
        </Stack>
        {loadingTickets ? <LinearProgress /> : null}
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Codigo</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Prioridad</TableCell>
                <TableCell>Area</TableCell>
                <TableCell>Asesor</TableCell>
                <TableCell>Creado</TableCell>
                <TableCell>SLA</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ticketsRows.map((r: any) => {
                const vencido = isSlaVencido(r);
                return (
                  <TableRow key={r.id_ticket} hover sx={vencido ? { bgcolor: 'rgba(211,47,47,0.06)' } : {}}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} component="a"
                        sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        onClick={() => navigate(`/tickets-soporte-cliente/detalle-ticket/${r.id_ticket}`)}>
                        {r.codigo_ticket}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{r.cliente_razon_social || '-'}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.cliente_identificacion}</Typography>
                    </TableCell>
                    <TableCell>
                      {r.estado_ticket ? (
                        <Chip label={r.estado_ticket.nombre} size="small"
                          sx={{ bgcolor: r.estado_ticket.color, color: '#fff', fontWeight: 600 }} />
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {r.prioridad ? (
                        <Chip label={r.prioridad.nombre} size="small" variant="outlined"
                          sx={{ borderColor: r.prioridad.color, color: r.prioridad.color }} />
                      ) : '-'}
                    </TableCell>
                    <TableCell>{r.area_destino?.nombre || '-'}</TableCell>
                    <TableCell>{r.usuario_asignado_nombre || '(Sin asignar)'}</TableCell>
                    <TableCell>{fmtFecha(r.fecha_creacion)}</TableCell>
                    <TableCell>
                      {!slaHabilitado ? <Typography variant="caption" color="text.secondary">OFF</Typography>
                        : vencido ? <Chip label="VENCIDO" size="small" color="error" />
                        : r.fecha_limite_sla ? <Typography variant="caption">{fmtFecha(r.fecha_limite_sla)}</Typography>
                        : '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
              {ticketsRows.length === 0 && !loadingTickets && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      Sin tickets para los filtros aplicados.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {ticketsTotal > pageSize && (
          <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
            <Pagination
              count={Math.ceil(ticketsTotal / pageSize)}
              page={page}
              onChange={(_, p) => setPage(p)}
              color="primary"
              size="small"
            />
          </Stack>
        )}
      </Paper>
    </Box>
  );
}

// ----------------------------------------------------------------------------
// KPI card aux. Compacta y reutilizable.
// ----------------------------------------------------------------------------
function KpiCard({ label, value, color, icon }: {
  label: string;
  value: number | string;
  color: 'primary' | 'info' | 'success' | 'warning' | 'error' | 'default';
  icon?: React.ReactNode;
}) {
  const palette: Record<string, string> = {
    primary: '#1976d2',
    info: '#0288d1',
    success: '#2e7d32',
    warning: '#ed6c02',
    error: '#d32f2f',
    default: '#616161',
  };
  return (
    <Grid item xs={6} sm={4} md={2.4}>
      <Card variant="outlined" sx={{ height: '100%', borderTop: `4px solid ${palette[color]}` }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="h4" fontWeight={700} sx={{ color: palette[color], mt: 0.5 }}>
                {value}
              </Typography>
            </Box>
            {icon && <Box sx={{ color: palette[color] }}>{icon}</Box>}
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
}
